import React, {useRef, useState} from "react";
import ReactGA from "react-ga4";
import {FormattedMessage, useIntl} from "react-intl";
import {useParams} from "react-router-dom";
import {RecipientTrackingPageParams, messageToDriver} from "../model/rideInfo";
import {closeModal, openModal} from "../utils/dialog";
import "../styles/Dialog.css";
import alertIcon from "../image/icon-alert-error.svg";
import {ReactComponent as MessageToDriverIcon} from "../image/message-to-driver-icon.svg";
import {SuccessDialog} from "./SuccessDialog";

export function MessageToCourier({receivedMessage}: {receivedMessage: string | undefined}) {
    const intl = useIntl();
    const {shareKey} = useParams<RecipientTrackingPageParams>();

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const ref = useRef<HTMLDialogElement>(null);
    const [message, setMessage] = useState("");
    const messageAlreadySent = receivedMessage != null || messageSent;
    const [showSuccess, setShowSuccess] = useState(false);

    const MAX_CHARS = 160;

    const overLimit = message.length > MAX_CHARS;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        setError(false);
    };

    const submitMessage = () => {
        setLoading(true);
        messageToDriver(shareKey, message.trim())
            .then(() => {
                setMessageSent(true);
                setShowSuccess(true);
                ReactGA.event({
                    category: shareKey,
                    action: "Leg message success",
                });
            })
            .catch(() => {
                setError(true);
                ReactGA.event({
                    category: shareKey,
                    action: "Leg message fail",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <button
                className={`stop-action message-button ${messageAlreadySent ? "show-message-button" : ""}`}
                onClick={() => openModal(ref.current)}
                data-cy="tracking-message-to-driver"
            >
                <MessageToDriverIcon />
                {messageAlreadySent ? (
                    <FormattedMessage id="show_message_to_courier" />
                ) : (
                    <FormattedMessage id="message_to_courier" />
                )}
            </button>
            <dialog className="modal" ref={ref}>
                {showSuccess ? (
                    <SuccessDialog
                        buttonClassName="understand-btn"
                        titleId="message_sent_title"
                        descriptionId="message_sent_info"
                        buttonId="understand"
                        onConfirm={() => {
                            setShowSuccess(false);
                            closeModal(ref.current);
                        }}
                    />
                ) : messageAlreadySent ? (
                    <div>
                        <div className="header">
                            <p className="title">
                                <FormattedMessage id="message_to_courier" />
                            </p>
                            <button
                                className="close-button"
                                onClick={() => closeModal(ref.current)}
                            >
                                ×
                            </button>
                        </div>

                        <p className="text-secondary">
                            <FormattedMessage id="message_sent_description" />
                        </p>

                        <p className="received-message">{receivedMessage ?? message}</p>
                        <button className="back-button" onClick={() => closeModal(ref.current)}>
                            <FormattedMessage id="back" />
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="header">
                            <p id="title" className="title">
                                <FormattedMessage id="message_to_courier" />
                            </p>
                            <button
                                data-cy="close-message-dialog-btn"
                                className="close-button"
                                onClick={() => closeModal(ref.current)}
                            >
                                ×
                            </button>
                        </div>
                        {receivedMessage != null ? (
                            <>
                                <p className="text">
                                    <FormattedMessage id="message_cant_be_edited" />
                                </p>
                                <p className="received-message">{receivedMessage}</p>
                                <button
                                    onClick={() => closeModal(ref.current)}
                                    className="secondary-button"
                                >
                                    <FormattedMessage id="back" />
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="text">
                                    <FormattedMessage id="message_to_courier_description" />
                                </p>
                                <textarea
                                    autoFocus
                                    disabled={loading || receivedMessage != null}
                                    className={`message-input ${overLimit ? "input-error" : ""}`}
                                    rows={4}
                                    onChange={handleChange}
                                    value={message}
                                    placeholder={intl.formatMessage({id: "message_placeholder"})}
                                    data-cy="tracking-message-to-driver-input"
                                ></textarea>
                                <div className="text-area-info">
                                    <span className="error-color">
                                        {overLimit && (
                                            <FormattedMessage
                                                id="message_maximum_characters"
                                                values={{MAX_CHARS}}
                                            />
                                        )}{" "}
                                    </span>

                                    <span className={`${overLimit ? "error-color" : ""}`}>
                                        {message.length} / {MAX_CHARS}
                                    </span>
                                </div>

                                {error && (
                                    <div className="alert">
                                        <img src={alertIcon} alt="" />
                                        <FormattedMessage id="send_message_failed" />
                                    </div>
                                )}
                                <button
                                    data-cy="confirm-message-to-driver-btn"
                                    onClick={submitMessage}
                                    className="primary-button"
                                    disabled={
                                        loading ||
                                        overLimit ||
                                        message.length === 0 ||
                                        receivedMessage != null
                                    }
                                >
                                    <FormattedMessage id="send_message" />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </dialog>
        </>
    );
}
